<template>
  <div class="main_div" :key="componentKey">
    <div style="height: auto">
      <n-space>
        <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
          <n-space item-style="display: flex;" align="center">
            <div style="font-size: 20px; width: 150px; font-weight: bold">사용자 관리</div>
          </n-space>
          <div class="content_delimiter_line"></div>

          <n-space vertical style="margin-top: 10px">
            <n-space align="baseline" item-style="display: flex;">
              <n-form-item label="검색" style="min-width: 350px" label-placement="left">
                <n-input-group>
                  <n-select v-model:value="searchType" :options="searchOptions" style="width: 120px" />
                  <n-input v-model:value="searchValue" placeholder="검색어를 입력하세요" clearable />
                </n-input-group>
              </n-form-item>
              <n-form-item style="min-width: 300px" label-placement="left"> </n-form-item>
              <n-form-item style="margin-bottom: 0">
                <n-button type="primary" @click="wrappedRefreshUsers()"> 조회 </n-button>
              </n-form-item>
            </n-space>
          </n-space>

          <Loading v-show="loading" />
          <div>
            <div v-if="listLoading" class="loading-indicator">사용자 목록을 불러오는 중...</div>
            <div v-if="error" class="error-message">{{ error }}</div>
            <div v-if="!listLoading && !error">
              <div class="table-container">
                <n-data-table
                  v-if="users.length > 0"
                  :columns="columns"
                  :data="users"
                  :pagination="false"
                  :bordered="true"
                />
                <p v-else>등록된 사용자가 없습니다.</p>
              </div>
              <div v-if="users.length > 0" class="pagination-controls" style="margin-top: 15px; text-align: center">
                <n-button @click="wrappedGoToPreviousPage" :disabled="currentPage === 1" style="margin-right: 8px">
                  이전 페이지
                </n-button>
                <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
                <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage"> 다음 페이지 </n-button>
              </div>
            </div>
          </div>
        </NCard>
      </n-space>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Member } from "@/model/member";
import { ref, onMounted, h } from "vue";
import { getCallable } from "@/config/env";
import {
  NSpace,
  NCard,
  NDataTable,
  NButton,
  NModal,
  NDescriptions,
  NDescriptionsItem,
  NForm,
  NFormItem,
  NInput,
  NInputGroup,
  FormInst,
  FormRules,
  NIcon,
  NSelect,
  NSpin,
  useMessage,
  useNotification,
  useDialog,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import { useRouter } from "vue-router";
import * as common from "@/assets/common/common";
import { useAuth } from "@/composables/useAuth";
import { USER_ROUTES } from "@/assets/route_constants";
import { usePagination } from "@/composables/usePagination"; // usePagination 임포트

const { loading, isLoggedIn } = useAuth();

const message = useMessage();
const notification = useNotification();
const dialog = useDialog();

const router = useRouter();
const getUsersCallable = getCallable("getMembers");

const searchValue = ref<string | null>(null);
const searchType = ref("name");

const searchOptions = [
  { label: "사용자명", value: "name" },
  { label: "이메일", value: "email" },
];

const {
  paginatedItems: users,
  listLoading,
  error,
  currentPage,
  hasNextPage,
  goToNextPage: composableGoToNextPage,
  goToPreviousPage: composableGoToPreviousPage,
  refreshData: composableRefreshUsers,
} = usePagination<Member>(async (params) => getUsersCallable(params), 10);

async function wrappedRefreshUsers() {
  loading.value = true;
  const params: { searchKey?: string; searchValue?: string } = {};

  if (searchValue.value && searchValue.value.trim() !== "") {
    params.searchKey = searchType.value;
    params.searchValue = searchValue.value.trim();
  }
  await composableRefreshUsers(params);
  loading.value = false;
}

const selectedMember = ref<Member | null>(null);
const componentKey = ref(0);
const isCheckingId = ref<boolean>(false);

const idCheckMessage = ref<string>("");
const isIdAvailable = ref<boolean | null>(null);

const columns = [
  {
    title: "ID",
    key: "SK",
    resizable: true,
    render(row: Member) {
      if (row.SK && row.SK.includes("#")) {
        return row.SK.split("#")[1];
      }
      return row.SK;
    },
  },
  {
    title: "사용자명",
    key: "name",
    resizable: true,
  },
  // {
  //   title: "회원등급",
  //   key: null,
  //   resizable: true,
  // },
  // {
  //   title: "성별",
  //   key: null,
  //   resizable: true,
  // },
  // {
  //   title: "지역",
  //   key: null,
  //   resizable: true,
  // },
  {
    title: "이메일",
    key: "email",
    resizable: true,
  },
  {
    title: "등록일",
    key: "createdDate",
    resizable: true,
    render(row: Member) {
      return common.getYYYYMMDD2(Number(row.createdDate));
    },
  },
];

async function wrappedGoToNextPage() {
  if (!hasNextPage.value) return;
  await composableGoToNextPage();
}

async function wrappedGoToPreviousPage() {
  if (currentPage.value <= 1) return;
  await composableGoToPreviousPage();
}

onMounted(() => {
  wrappedRefreshUsers(); // 초기 데이터 로드
});
</script>

<style scoped>
.registration-form-content p {
  margin: 8px 0;
  line-height: 1.6;
  color: #333; /* 모달 내부 텍스트 색상 명확히 */
}

.description-item-label-fixed-width {
  width: 80px; /* "기관 ID" 레이블과 유사한 너비로 조정 */
  display: inline-block; /* 너비 적용을 위해 */
}
.description-item-content-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.table-container {
  width: 100%; /* 부모 요소의 너비를 최대한 활용 */
  overflow-x: auto; /* 내용이 넘칠 경우 가로 스크롤바 표시 */
}
</style>
